import { useState, useEffect } from 'react';
import { createTool, updateTool } from '../../utils/api';

const CATEGORIES = ['power', 'hand', 'measuring', 'safety', 'garden', 'plumbing', 'electrical', 'automotive', 'battery', 'other'];
const CONDITIONS = ['excellent', 'good', 'fair', 'poor'];

const blank = {
  name: '', brand: '', modelNumber: '', category: 'other', condition: 'good',
  location: '', serialNumber: '', purchaseDate: '', purchasePrice: '', warrantyExpiry: '', notes: '',
  platform: '', voltage: '', ampHours: '', quantity: 1,
};

const ToolForm = ({ existing, onClose, onSaved }) => {
  const [form, setForm] = useState(existing ? {
    ...blank,
    ...existing,
    purchaseDate: existing.purchaseDate ? existing.purchaseDate.slice(0, 10) : '',
    warrantyExpiry: existing.warrantyExpiry ? existing.warrantyExpiry.slice(0, 10) : '',
    purchasePrice: existing.purchasePrice ?? '',
    ampHours: existing.ampHours ?? '',
    quantity: existing.quantity ?? 1,
    platform: existing.platform ?? '',
    voltage: existing.voltage ?? '',
  } : blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isBattery = form.category === 'battery';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.purchasePrice === '') delete payload.purchasePrice;
      else payload.purchasePrice = parseFloat(payload.purchasePrice);
      if (!payload.purchaseDate) delete payload.purchaseDate;
      if (!payload.warrantyExpiry) delete payload.warrantyExpiry;
      if (payload.ampHours === '') delete payload.ampHours;
      else if (payload.ampHours !== undefined) payload.ampHours = parseFloat(payload.ampHours);
      payload.quantity = parseInt(payload.quantity, 10) || 1;

      const { data } = existing
        ? await updateTool(existing._id, payload)
        : await createTool(payload);
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{existing ? 'Edit' : 'Add'} {isBattery ? 'Battery' : 'Tool'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="modal-form" onSubmit={handleSubmit}>
          <label>Name *
            <input name="name" value={form.name} onChange={set} placeholder={isBattery ? 'e.g. M18 5.0Ah Battery' : 'e.g. Cordless Drill'} autoFocus required />
          </label>
          <div className="form-row">
            <label>Brand
              <input name="brand" value={form.brand} onChange={set} placeholder="e.g. Milwaukee" />
            </label>
            <label>Model Number
              <input name="modelNumber" value={form.modelNumber} onChange={set} placeholder="e.g. 48-11-1850" />
            </label>
          </div>
          <div className="form-row">
            <label>Category
              <select name="category" value={form.category} onChange={set}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </label>
            <label>Condition
              <select name="condition" value={form.condition} onChange={set}>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </label>
          </div>

          {isBattery && (
            <div className="form-row">
              <label>Platform
                <input name="platform" value={form.platform} onChange={set} placeholder="e.g. M18, 20V MAX" />
              </label>
              <label>Voltage
                <input name="voltage" value={form.voltage} onChange={set} placeholder="e.g. 18V" />
              </label>
            </div>
          )}
          {isBattery && (
            <div className="form-row">
              <label>Amp Hours (Ah)
                <input type="number" name="ampHours" value={form.ampHours} onChange={set} min="0" step="0.1" placeholder="e.g. 5.0" />
              </label>
              <label>Quantity
                <input type="number" name="quantity" value={form.quantity} onChange={set} min="1" step="1" />
              </label>
            </div>
          )}

          <label>Storage Location
            <input name="location" value={form.location} onChange={set} placeholder="e.g. Garage shelf B" />
          </label>
          <div className="form-row">
            <label>Purchase Date
              <input type="date" name="purchaseDate" value={form.purchaseDate} onChange={set} />
            </label>
            <label>Purchase Price
              <input type="number" name="purchasePrice" value={form.purchasePrice} onChange={set} min="0" step="0.01" placeholder="0.00" />
            </label>
          </div>
          {!isBattery && (
            <div className="form-row">
              <label>Serial Number
                <input name="serialNumber" value={form.serialNumber} onChange={set} placeholder="Optional" />
              </label>
              <label>Warranty Expiry
                <input type="date" name="warrantyExpiry" value={form.warrantyExpiry} onChange={set} />
              </label>
            </div>
          )}
          {isBattery && (
            <label>Warranty Expiry
              <input type="date" name="warrantyExpiry" value={form.warrantyExpiry} onChange={set} />
            </label>
          )}
          <label>Notes
            <textarea name="notes" value={form.notes} onChange={set} rows={2} placeholder="Any notes…" />
          </label>
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : existing ? 'Save Changes' : isBattery ? 'Add Battery' : 'Add Tool'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ToolForm;
